import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FaArrowLeft,
  FaInstagram,
  FaLinkedin,
  FaTelegram,
  FaTwitter,
  FaWhatsapp,
  FaYoutube,
} from 'react-icons/fa';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { useLocale } from '../context/LocaleContext';
import './Footer.css';

const quickLinks = [
  { to: '/services', key: 'services' },
  { to: '/products', key: 'products' },
  { to: '/about', key: 'about' },
  { to: '/contact', key: 'contact' },
];

const serviceLinks = [
  'webDesign',
  'brandIdentity',
  'uiDesign',
  'wordpress',
];

const Footer = () => {
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const { locale, localePath } = useLocale();
  const isHomePage = pathname === `/${locale.code}`;
  const currentYear = new Date().getFullYear();
  const { settings } = useSiteSettings();
  const { general, contact } = settings;
  const socialLinks = [
    ['instagram', contact.social.instagram, FaInstagram, 'Instagram'],
    ['telegram', contact.social.telegram, FaTelegram, 'Telegram'],
    ['linkedin', contact.social.linkedin, FaLinkedin, 'LinkedIn'],
    ['whatsapp', contact.social.whatsapp, FaWhatsapp, 'WhatsApp'],
    ['x', contact.social.x, FaTwitter, 'X'],
    ['youtube', contact.social.youtube, FaYoutube, 'YouTube'],
  ].filter(([, url]) => Boolean(url));

  return (
    <footer className="site-footer">
      <div className="site-footer__glow site-footer__glow--one" aria-hidden="true" />
      <div className="site-footer__glow site-footer__glow--two" aria-hidden="true" />

      <div className="site-footer__container">
        {isHomePage && (
          <div className="site-footer__cta">
            <div className="site-footer__cta-copy">
              <span className="site-footer__eyebrow">{t('footer.eyebrow')}</span>
              <h2>{t('footer.cta')}</h2>
            </div>
            <Link to={localePath('/services')} className="site-footer__cta-button">
              <span>{t('footer.start')}</span>
              <span className="site-footer__cta-icon"><FaArrowLeft /></span>
            </Link>
          </div>
        )}

        <div className="site-footer__main">
          <div className="site-footer__brand">
            <p className="site-footer__slogan">
              {t('footer.slogan')}
            </p>
            <p className="site-footer__description">
              {general.footerDescription || t('footer.description')}
            </p>

            {socialLinks.length > 0 && (
              <div className="site-footer__socials" aria-label={t('footer.social')}>
                {socialLinks.map(([key, url, Icon, label]) => (
                  <a
                    key={key}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="site-footer__social"
                    aria-label={label}
                    title={label}
                  >
                    <Icon />
                  </a>
                ))}
              </div>
            )}
          </div>

          <nav className="site-footer__column site-footer__navigation" aria-label={t('footer.linksLabel')}>
            <h3><span>01</span> {t('footer.quick')}</h3>
            <ul className="site-footer__links site-footer__links--quick">
              {quickLinks.map((item) => (
                <li key={item.to}>
                  <Link to={localePath(item.to)}>{t(`nav.${item.key}`)}</Link>
                </li>
              ))}
            </ul>
          </nav>

          <div className="site-footer__column">
            <h3><span>02</span> {t('footer.services')}</h3>
            <ul className="site-footer__links">
              {serviceLinks.map((service) => (
                <li key={service}>
                  <Link to={localePath('/services')}>{t(`serviceNames.${service}`)}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="site-footer__column site-footer__seal">
            <h3><span>03</span> {t('footer.trust')}</h3>
            {general.trustSealUrl && general.trustSealImageUrl && (
              <a
                className="site-footer__trust"
                referrerPolicy="origin"
                target="_blank"
                rel="noopener noreferrer"
                href={general.trustSealUrl}
                aria-label={t('footer.trustView')}
              >
                <img
                  width="125"
                  height="136"
                  referrerPolicy="origin"
                  src={general.trustSealImageUrl}
                  alt={t('footer.trustAlt')}
                />
              </a>
            )}
          </div>
        </div>

        <div className="site-footer__bottom">
          <p>
            © {currentYear} <strong>{general.siteTitle || t('home.title')}</strong>
            {general.copyrightText ? `؛ ${general.copyrightText}` : ''}
          </p>

          <div className="site-footer__bottom-side">
            <span><i aria-hidden="true" /> {t('footer.location')}</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
